import React, { useState, useEffect } from 'react';
import { Modal } from '../../components/common/Modal';
import { Tabs } from '../../components/common/Tabs';
import { shippingApi } from '../../api/shippingApi';
import { BulkShipmentPlanForm } from './BulkShipmentPlanForm';

export const PurchaseOrderModal = ({ isOpen, onClose, hacchuNo, onBulkApplied }) => {
  const [activeTab, setActiveTab] = useState('bulk');
  const [summary, setSummary] = useState(null);
  const [details, setDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDatacodes, setSelectedDatacodes] = useState(new Set());

  useEffect(() => {
    if (isOpen && hacchuNo) {
      loadData();
    } else {
      // モーダルを閉じる際に状態をリセット
      setSummary(null);
      setDetails([]);
      setSelectedDatacodes(new Set());
      setActiveTab('bulk');
    }
  }, [isOpen, hacchuNo]);

  // データ変更イベントをリッスン
  useEffect(() => {
    const handleDataChange = () => {
      if (isOpen && hacchuNo) {
        loadData();
      }
    };
    
    window.addEventListener('purchaseOrderDataChanged', handleDataChange);
    return () => {
      window.removeEventListener('purchaseOrderDataChanged', handleDataChange);
    };
  }, [isOpen, hacchuNo]);

  const loadData = async () => {
    if (!hacchuNo) return;
    
    setLoading(true);
    try {
      // 基本情報と明細一覧を並行取得
      const [summaryResponse, detailsResponse] = await Promise.all([
        shippingApi.getPurchaseOrderSummary(hacchuNo),
        shippingApi.getPurchaseOrderDetails(hacchuNo)
      ]);
      
      // レスポンスの構造を確認して適切に処理
      const summaryData = summaryResponse?.data || summaryResponse;
      const detailsData = detailsResponse?.data || detailsResponse;
      
      setSummary(summaryData);
      setDetails(Array.isArray(detailsData) ? detailsData : []);
    } catch (error) {
      console.error('データ取得エラー:', error);
      console.error('エラー詳細:', error.response?.data || error.message);
      alert(`データの取得に失敗しました: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxChange = (datacode, checked) => {
    const newSelected = new Set(selectedDatacodes);
    if (checked) {
      newSelected.add(datacode);
    } else {
      newSelected.delete(datacode);
    }
    setSelectedDatacodes(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedDatacodes.size === details.length) {
      setSelectedDatacodes(new Set());
    } else {
      setSelectedDatacodes(new Set(details.map(d => d.hacchu_datacode)));
    }
  };

  if (!isOpen) return null;

  const modalTitle = (
    <div>
      <div className="text-lg font-semibold text-slate-900">発注No: {hacchuNo || ''}</div>
      {summary && (
        <div className="text-sm text-slate-600 mt-1 flex flex-wrap items-center gap-x-6 gap-y-1">
          <span>
            <span className="font-medium">発注日:</span>{' '}
            {summary.hacchu_date || '-'}
          </span>
          <span>
            <span className="font-medium">明細数:</span>{' '}
            {summary.detail_count}件
          </span>
        </div>
      )}
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      size="xl"
    >
      {loading ? (
        <div className="p-8 text-center text-slate-500">読み込み中...</div>
      ) : (
        <div className="space-y-4">
          <Tabs
            tabs={[
              { id: 'bulk', label: '一括操作' },
              { id: 'split', label: '分納履歴' },
            ]}
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />

          {activeTab === 'bulk' && (
            <BulkOperationTab
              hacchuNo={hacchuNo}
              details={details}
              selectedDatacodes={selectedDatacodes}
              onCheckboxChange={handleCheckboxChange}
              onSelectAll={handleSelectAll}
              onUpdate={loadData}
              onBulkApplied={onBulkApplied}
            />
          )}

          {activeTab === 'split' && (
            <SplitManagementTab details={details} />
          )}
        </div>
      )}
    </Modal>
  );
};


const hasSplitPlans = (detail) => Array.isArray(detail.split_plans) && detail.split_plans.length > 0;

const TwoLineCell = ({ primary, secondary }) => (
  <div className="leading-snug">
    <div className="text-slate-900">{primary || '-'}</div>
    <div className="text-slate-600 text-xs">{secondary || '-'}</div>
  </div>
);

// 一括操作タブ（明細一覧と一括操作を統合）
const BulkOperationTab = ({
  hacchuNo,
  details,
  selectedDatacodes,
  onCheckboxChange,
  onSelectAll,
  onUpdate,
  onBulkApplied,
}) => {
  const [saving, setSaving] = useState(false);

  const selectedCount = selectedDatacodes.size;
  const targetDetails = details.filter((detail) => selectedDatacodes.has(detail.hacchu_datacode));
  const containsSplitPlans = targetDetails.some((detail) => hasSplitPlans(detail));

  const handleApply = async (payload) => {
    setSaving(true);
    try {
      await shippingApi.bulkUpdatePurchaseOrder(hacchuNo, payload);
      onUpdate();
      if (onBulkApplied) {
        onBulkApplied();
      }
      return { success: true };
    } catch (error) {
      console.error('一括更新エラー:', error);
      const message = error.response?.data?.detail || '一括更新に失敗しました';
      return { success: false, error: message };
    } finally {
      setSaving(false);
    }
  };

  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '-';
    return num.toLocaleString();
  };

  return (
    <div className="space-y-4">
      {/* 明細選択エリア */}
      <div className="bg-white rounded-lg border border-slate-200 p-4">
        <div className="flex items-center gap-4 mb-4 flex-wrap">
          <h3 className="font-semibold text-lg text-slate-900">明細選択</h3>
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              checked={selectedDatacodes.size === details.length && details.length > 0}
              onChange={onSelectAll}
              className="w-4 h-4 text-slate-600 border-slate-300 rounded focus:ring-slate-500"
            />
            <span>全選択</span>
          </label>
          <span className="text-sm text-slate-500">({selectedDatacodes.size}件選択中)</span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700 border-b border-slate-200 w-12"></th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700 border-b border-slate-200">製品CD</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700 border-b border-slate-200">製品名 / 寸法</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700 border-b border-slate-200">数量</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700 border-b border-slate-200">得意先 / 納入先</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700 border-b border-slate-200">発送日</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-100">
              {details.map((detail) => {
                const detailHasSplitPlans = hasSplitPlans(detail);
                const shipmentDateSource = detailHasSplitPlans
                  ? detail.latest_shipment_date
                  : (detail.shipment_date || detail.latest_shipment_date);

                return (
                  <tr key={detail.hacchu_datacode} className="hover:bg-slate-50">
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selectedDatacodes.has(detail.hacchu_datacode)}
                        onChange={(e) => {
                          onCheckboxChange(detail.hacchu_datacode, e.target.checked);
                        }}
                        className="w-4 h-4 text-slate-600 border-slate-300 rounded focus:ring-slate-500"
                      />
                    </td>
                    <td className="px-3 py-2 text-slate-900 whitespace-nowrap">{detail.product_disp || '-'}</td>
                    <td className="px-3 py-2">
                      <TwoLineCell
                        primary={detail.product_name}
                        secondary={detail.product_size}
                      />
                    </td>
                    <td className="px-3 py-2 text-slate-900 whitespace-nowrap">
                      {formatNumber(detail.hacchu_amount)} {detail.tani || ''}
                    </td>
                    <td className="px-3 py-2">
                      <TwoLineCell
                        primary={detail.customer_name}
                        secondary={detail.nonyusaki}
                      />
                    </td>
                    <td className="px-3 py-2 text-slate-900 whitespace-nowrap">
                      {formatDateDisplay(shipmentDateSource)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* 一括設定エリア */}
      <BulkShipmentPlanForm
        targetDetails={targetDetails}
        selectedCount={selectedCount}
        selectedDatacodes={selectedDatacodes}
        containsSplitPlans={containsSplitPlans}
        onApply={handleApply}
        saving={saving}
      />
    </div>
  );
};

// 分納履歴タブ（表示のみ）
const SplitManagementTab = ({ details }) => {
  return (
    <div className="space-y-4">
      {details.map((detail) => (
        <div key={detail.hacchu_datacode} className="bg-white rounded-lg border border-slate-200 p-4">
          <h3 className="font-semibold text-sm mb-3 text-slate-900">
            {detail.product_disp || detail.product_name} - {detail.hacchu_amount}{detail.tani || ''}
          </h3>
          {detail.split_plans && detail.split_plans.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700 border-b border-slate-200">分納回</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700 border-b border-slate-200">数量</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700 border-b border-slate-200">発送日</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-100">
                  {detail.split_plans.map((plan) => (
                    <tr key={plan.split_no} className="hover:bg-slate-50">
                      <td className="px-3 py-2 text-slate-900">{plan.split_no}</td>
                      <td className="px-3 py-2 text-slate-900">{plan.planned_qty.toLocaleString()}</td>
                      <td className="px-3 py-2 text-slate-900">
                        {plan.shipment_date 
                          ? new Date(plan.shipment_date).toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' })
                          : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-slate-500">分納計画がありません</p>
          )}
        </div>
      ))}
    </div>
  );
};
